import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ButtonAppearance, Icon } from '@components/shared';
import { withFocusInteraction } from '@utils/index';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';

export interface ShowMoreProps {
  /** Ref to the card element */
  cardRef?: React.RefObject<HTMLDivElement>;
  /** Shows the button to share on PDF */
  shareOnPdf?: boolean;
  /** Function to be called when PDF button is clicked */
  onGeneratePdf?: () => void;
  /** Flag that indicates whether we need to show the View updates option */
  areHistoricalNotifications?: boolean;
  /** Function to be called when View updates option is clicked */
  onViewUpdates?: () => void;
  /** Indicates position of the popover*/
  alignToRight?: boolean;
}

export const ShowMore: React.FC<ShowMoreProps> = ({
  cardRef,
  shareOnPdf,
  onGeneratePdf,
  areHistoricalNotifications,
  onViewUpdates,
  alignToRight,
}: ShowMoreProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleButtonClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleOnGeneratePDF = (event: React.MouseEvent): void => {
    if (onGeneratePdf) {
      event.stopPropagation();
      event.preventDefault();
      onGeneratePdf();
      setIsOpen(!isOpen);
    }
  };

  const handleOnViewUpdates = (event: React.MouseEvent): void => {
    if (onViewUpdates) {
      event.stopPropagation();
      event.preventDefault();
      onViewUpdates();
      setIsOpen(!isOpen);
    }
  };

  const onKeyDownGeneratePdf = useCallback(
    withFocusInteraction(() => {
      if (onGeneratePdf) {
        onGeneratePdf();
      }
    }),
    [onGeneratePdf],
  );

  const onKeyDownViewUpdates = useCallback(
    withFocusInteraction(() => {
      if (onViewUpdates) {
        onViewUpdates();
      }
    }),
    [onViewUpdates],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickOnThisCard = (cardRef?.current || dropdownRef.current)?.contains(event.target as Node);

      if (!isClickOnThisCard) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, cardRef]);

  return (
    <div css={css({ width: 'fit-content', position: 'relative' })} data-testid="show-more" ref={dropdownRef}>
      <Button
        label=""
        appearance={ButtonAppearance.ICON}
        onClick={handleButtonClick}
        selected={isOpen}
        iconBefore={<Icon type="more-vertical" />}
        iconType="more-vertical"
      />
      {isOpen && (
        <div
          data-testid="show-more-dropdown"
          css={[showMoreDropdownStyles, alignToRight && css({ right: 'unset', textAlign: 'left' })]}
        >
          {shareOnPdf && onGeneratePdf && (
            <div tabIndex={0} css={optionStyles} onClick={handleOnGeneratePDF} onKeyDown={onKeyDownGeneratePdf}>
              <span css={optionTextStyles}>Export to PDF</span>
            </div>
          )}
          {areHistoricalNotifications && onViewUpdates && (
            <div tabIndex={0} css={optionStyles} onClick={handleOnViewUpdates} onKeyDown={onKeyDownViewUpdates}>
              <span css={optionTextStyles}>View updates</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const showMoreDropdownStyles = css({
  position: 'absolute',
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: '7px 0',
  marginTop: token('space.050'),
  zIndex: 2,
  color: token('color.text'),
  backgroundColor: token('color.background.input'),
  boxShadow: token('elevation.shadow.overlay'),
});

const optionStyles = css({
  width: '220px',
  padding: '7px 24px',
  transition: 'background',
  transitionDuration: '300ms',
  transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
  cursor: 'pointer',

  ':hover': {
    backgroundColor: token('color.background.neutral.subtle.hovered'),
  },
});

const optionTextStyles = css({
  fontSize: '16px',
  lineHeight: '20px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: '400',
});
